// For a forgotten passphrase, which nothing can recover. Nothing in Locked can be opened without it,
// by the person or anyone else, so resetting deletes all of it for good and lets a new passphrase be
// set. Since that can't be undone, the dialog says how much goes and asks for a phrase to be typed
// before it will.
import PropTypes from 'prop-types'
import {useApolloClient, useMutation} from '@apollo/client/react'
import {useState} from 'react'

import DeleteForeverIcon from '@mui/icons-material/DeleteForeverOutlined'
import Dialog from '@mui/material/Dialog'

import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import {LOCKED} from '../../graphql/queries/locked'
import {RESET_LOCKED} from '../../graphql/mutations/locked'
import {STORAGE} from '../../graphql/queries/storage'
import {sealLocked} from '../../lib/lockedTools'

import './lockedDialog.scss'

const CONFIRMATION = 'delete everything'

const ResetLockedDialog = ({itemCount, onClose, onReset}) => {
  const client = useApolloClient()
  const [typed, setTyped] = useState('')
  const [resetLocked, {loading, error}] = useMutation(RESET_LOCKED)

  const handleSubmit = event => {
    event.preventDefault()
    if (typed !== CONFIRMATION) return

    resetLocked()
      .then(() => {
        sealLocked()

        return client.refetchQueries({include: [LOCKED, STORAGE]})
      })
      .then(onReset)
      // Shown from the mutation's own error state below.
      .catch(() => {})
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby='reset-locked-title'
      slotProps={{paper: {className: 'locked-dialog', component: 'form', onSubmit: handleSubmit}}}>
      <div className='dialog-head'>
        <DeleteForeverIcon />
        <h2 className='dialog-title' id='reset-locked-title'>Reset Locked</h2>
      </div>
      <Text className='dialog-body'>
        Without your passphrase nothing in Locked can be opened, by you or anyone else. Resetting deletes everything in it
        for good{itemCount ? ` — ${itemCount} item${itemCount === 1 ? '' : 's'} at its top and all they hold` : ''} — so you
        can set a new passphrase. This can’t be undone.
      </Text>

      <label className='dialog-field'>
        <span className='field-label'>Type “{CONFIRMATION}” to confirm</span>
        <input type='text' value={typed} onChange={event => setTyped(event.target.value)} autoComplete='off' autoFocus />
      </label>
      {error && <Text className='dialog-error' role='alert'>Locked couldn’t be reset. Try again.</Text>}

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Cancel</BaseButton>
        <BaseButton className='dialog-confirm' color='error' type='submit' disabled={loading || typed !== CONFIRMATION}>
          Delete everything in Locked
        </BaseButton>
      </div>
    </Dialog>
  )
}

ResetLockedDialog.propTypes = {
  // What sealed Locked shows at its top, as far as the drive can tell.
  itemCount: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired
}

export default ResetLockedDialog
